import {
  caixaDaCamadaDeImagem,
  caixaDaCamadaDeTexto,
  type Camada,
  type Tamanho,
} from "@foto/shared";
import { estimarBlocoDeTexto } from "./camadas-da-celula.js";
import { figurinhaDe, type FigurinhasDasCamadas } from "./figurinhas-das-camadas.js";
import type { ItemDaCelula } from "./gestos.js";

/**
 * O tamanho natural da imagem de uma camada. Sem a figurinha (carregando, ou indisponível) a camada é um
 * quadrado: a caixa existe, dá para selecionar, mover e remover o marcador.
 */
export function imagemDaCamada(
  camada: Camada,
  figurinhas: FigurinhasDasCamadas | undefined,
): Tamanho {
  if (camada.tipo !== "imagem") return { largura: 1, altura: 1 };
  const f = figurinhaDe(figurinhas, camada.figurinhaId);
  return f && f.largura > 0 && f.altura > 0
    ? { largura: f.largura, altura: f.altura }
    : { largura: 1, altura: 1 };
}

/**
 * A caixa de cada camada na célula, em pixels dela (`celula`): a imagem sai da proporção da figurinha; o texto,
 * do bloco que a tela mediu no DOM (`blocos`) — ou de uma estimativa, no primeiro quadro, enquanto o elemento
 * não foi medido nem a fonte chegou.
 */
export function itensDaCelula(
  camadas: readonly Camada[],
  figurinhas: FigurinhasDasCamadas | undefined,
  blocos: Readonly<Record<string, Tamanho>>,
  celula: Tamanho,
): ItemDaCelula[] {
  return camadas.map((camada) => ({
    id: camada.id,
    camada,
    caixa:
      camada.tipo === "imagem"
        ? caixaDaCamadaDeImagem(camada, imagemDaCamada(camada, figurinhas), celula)
        : caixaDaCamadaDeTexto(
            camada,
            blocos[camada.id] ?? estimarBlocoDeTexto(camada, celula),
            celula,
          ),
  }));
}

/**
 * O maior retângulo da proporção `proporcao` (largura ÷ altura) que cabe em `area` com `margem` de cada lado.
 * É o tamanho da célula na tela do editor; a margem é o espaço para as alças que saem da célula.
 */
export function encaixarCelula(area: Tamanho, proporcao: number, margem: number): Tamanho {
  const larguraLivre = Math.max(0, area.largura - 2 * margem);
  const alturaLivre = Math.max(0, area.altura - 2 * margem);
  const largura = Math.min(larguraLivre, alturaLivre * proporcao);
  return { largura, altura: proporcao > 0 ? largura / proporcao : 0 };
}
